import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Collapse
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { environmentApi } from '../../services/api';

export interface CreateEnvironmentInlineProps {
  onEnvironmentCreated: (environment: { id: string; name: string; basePrefix: string }) => void;
  onError?: (error: string) => void;
}

export const CreateEnvironmentInline: React.FC<CreateEnvironmentInlineProps> = ({
  onEnvironmentCreated,
  onError
}) => {
  const [showForm, setShowForm] = useState(false);
  const [environmentName, setEnvironmentName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!environmentName.trim()) {
      setError('Environment name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create the environment
      const result = await environmentApi.createEnvironment(environmentName.trim());

      // Since newly created environments don't have bucket configs yet,
      // we'll use the environment name as both ID and prefix
      onEnvironmentCreated({
        id: result.name, // Use name as ID temporarily
        name: result.name,
        basePrefix: result.name // Use name as prefix
      });

      // Reset form
      setEnvironmentName('');
      setShowForm(false);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to create environment';
      setError(errorMsg);
      if (onError) {
        onError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!showForm) {
    return (
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => setShowForm(true)}
        fullWidth
        sx={{ mb: 2 }}
      >
        Create New S3 Environment
      </Button>
    );
  }

  return (
    <Collapse in={showForm}>
      <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
        <TextField
          fullWidth
          label="Environment Name"
          value={environmentName}
          onChange={(e) => setEnvironmentName(e.target.value)}
          placeholder="e.g., production, staging, dev"
          disabled={loading}
          autoFocus
          sx={{ mb: 2 }}
          helperText="Enter a unique name for the new S3 environment"
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={loading || !environmentName.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {loading ? 'Creating...' : 'Create'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setShowForm(false);
              setEnvironmentName('');
              setError(null);
            }}
            disabled={loading}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Collapse>
  );
};
